import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, CalendarDays, Save, Copy, ChevronRight, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../../api';

// ── Helpers ──────────────────────────────────────────────────────────────────

function fmtDate(d) {
  try { return new Date(d + 'T00:00:00').toLocaleDateString('fr-FR', { day: '2-digit', month: 'long' }); }
  catch { return d; }
}

function yearOf(d) {
  return d ? d.slice(0, 4) : null;
}

// ── Sous-composant : liste des années ────────────────────────────────────────

function YearSidebar({ years, selected, onSelect }) {
  return (
    <div className="w-28 flex-shrink-0 space-y-0.5">
      <p className="text-xs font-bold uppercase tracking-widest mb-2 px-2"
        style={{ color: 'var(--text-muted)' }}>Années</p>
      {years.length === 0 && (
        <p className="px-2 text-xs" style={{ color: 'var(--text-disabled)' }}>–</p>
      )}
      {years.map(({ year, count }) => {
        const active = selected === year;
        return (
          <button
            key={year}
            data-testid={`year-tab-${year}`}
            onClick={() => onSelect(year)}
            className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-colors"
            style={{
              background: active ? 'var(--primary-solid)' : 'transparent',
              color: active ? '#fff' : 'var(--text)',
            }}
          >
            <span>{year}</span>
            <span className="text-xs px-1.5 py-0.5 rounded-full font-semibold"
              style={{
                background: active ? 'rgba(255,255,255,0.25)' : 'var(--bg-subtle)',
                color: active ? '#fff' : 'var(--text-muted)',
              }}>{count}</span>
          </button>
        );
      })}
    </div>
  );
}

// ── Panneau de duplication ────────────────────────────────────────────────────

function DuplicatePanel({ agences, sourceAgence, selectedYear, onClose }) {
  const [targetAgence, setTargetAgence] = useState('');
  const [yearMode, setYearMode]         = useState(selectedYear || '');
  const [confirming, setConfirming]     = useState(false);
  const [copying, setCopying]           = useState(false);

  const otherAgences = agences.filter(a => a.agence_id !== sourceAgence);
  const sourceObj    = agences.find(a => a.agence_id === sourceAgence);

  const handleCopy = async () => {
    if (!targetAgence) { toast.error('Sélectionnez une agence cible'); return; }
    if (!confirming) { setConfirming(true); return; }

    setCopying(true);
    try {
      // 1. Lire les données source depuis la DB
      const srcRes = await api.getJoursFeries(sourceAgence);
      let srcJours = srcRes.data || [];

      // 2. Filtrer par année si nécessaire
      if (yearMode) {
        srcJours = srcJours.filter(j => yearOf(j.date) === yearMode);
      }

      if (srcJours.length === 0) {
        toast.warning(`Aucun jour férié à copier${yearMode ? ` pour ${yearMode}` : ''}`);
        setCopying(false);
        return;
      }

      // 3. Charger l'existant de la cible, fusionner (écraser = remplacer l'année concernée)
      const tgtRes = await api.getJoursFeries(targetAgence);
      let tgtJours = tgtRes.data || [];

      if (yearMode) {
        // Écraser uniquement l'année sélectionnée sur la cible
        tgtJours = tgtJours.filter(j => yearOf(j.date) !== yearMode);
      } else {
        // Écraser tout
        tgtJours = [];
      }

      const merged = [...tgtJours, ...srcJours]
        .sort((a, b) => a.date.localeCompare(b.date));

      // 4. Enregistrer
      await api.setJoursFeries(targetAgence, merged);

      const tgtObj = agences.find(a => a.agence_id === targetAgence);
      toast.success(`${srcJours.length} jour(s) copié(s) vers ${tgtObj?.nom || 'l\'agence'}`);
      onClose();
    } catch {
      toast.error('Erreur lors de la copie');
    } finally {
      setCopying(false);
      setConfirming(false);
    }
  };

  return (
    <div className="rounded-xl border p-4 space-y-4"
      style={{ borderColor: 'var(--border)', background: 'var(--bg-subtle)' }}>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Copy size={14} style={{ color: 'var(--primary-solid)' }} />
          <span className="text-sm font-semibold" style={{ color: 'var(--text)' }}>Dupliquer vers une autre agence</span>
        </div>
        <button onClick={onClose} className="text-xs px-2 py-1 rounded hover:bg-red-50 transition-colors"
          style={{ color: 'var(--text-muted)' }}>Fermer</button>
      </div>

      <div className="grid grid-cols-3 gap-3 items-end">

        {/* Source */}
        <div className="space-y-1">
          <p className="text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>Source</p>
          <div className="px-3 py-2 rounded-lg border text-sm font-medium"
            style={{ borderColor: 'var(--border)', background: 'white', color: 'var(--text)' }}>
            {sourceObj?.nom || '–'}
          </div>
        </div>

        {/* Année à copier */}
        <div className="space-y-1">
          <p className="text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>Année à copier</p>
          <select value={yearMode} onChange={e => { setYearMode(e.target.value); setConfirming(false); }}
            className="w-full px-3 py-2 rounded-lg border text-sm"
            style={{ borderColor: 'var(--border)', background: 'white' }}>
            <option value="">Toutes les années</option>
            {selectedYear && <option value={selectedYear}>{selectedYear} uniquement</option>}
          </select>
        </div>

        {/* Cible */}
        <div className="space-y-1">
          <p className="text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>Agence cible</p>
          <select value={targetAgence} onChange={e => { setTargetAgence(e.target.value); setConfirming(false); }}
            data-testid="duplicate-target-agence"
            className="w-full px-3 py-2 rounded-lg border text-sm"
            style={{ borderColor: 'var(--border)', background: 'white' }}>
            <option value="">Sélectionner…</option>
            {otherAgences.map(a => (
              <option key={a.agence_id} value={a.agence_id}>{a.nom}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Zone de confirmation */}
      {confirming && targetAgence && (
        <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg border"
          style={{ borderColor: '#FCA5A5', background: '#FEF2F2' }}>
          <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" style={{ color: '#EF4444' }} />
          <p className="text-xs" style={{ color: '#B91C1C' }}>
            Les jours fériés {yearMode ? `de ${yearMode}` : 'de toutes les années'} de l'agence <strong>{agences.find(a => a.agence_id === targetAgence)?.nom}</strong> seront <strong>écrasés</strong>. Cliquez à nouveau pour confirmer.
          </p>
        </div>
      )}

      <div className="flex justify-end">
        <button
          onClick={handleCopy}
          disabled={copying || !targetAgence}
          data-testid="duplicate-confirm-btn"
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-50 transition-colors"
          style={{ background: confirming ? '#EF4444' : 'var(--primary-gradient)' }}>
          <Copy size={13} />
          {copying ? 'Copie en cours…' : confirming ? 'Confirmer l\'écrasement' : 'Copier →'}
        </button>
      </div>
    </div>
  );
}

// ── Composant principal ───────────────────────────────────────────────────────

export default function JoursFeries() {
  const [agences, setAgences]               = useState([]);
  const [selectedAgence, setSelectedAgence] = useState('');
  const [jours, setJours]                   = useState([]);
  const [loading, setLoading]               = useState(false);
  const [saving, setSaving]                 = useState(false);
  const [selectedYear, setSelectedYear]     = useState(String(new Date().getFullYear()));
  const [newDate, setNewDate]               = useState('');
  const [newNom, setNewNom]                 = useState('');
  const [showDuplicate, setShowDuplicate]   = useState(false);

  // Charger agences
  useEffect(() => {
    api.getSocietes().then(r => {
      const allAgences = (r.data || []).flatMap(s =>
        (s.agences || []).map(a => ({ ...a, societe_nom: s.nom }))
      );
      setAgences(allAgences);
      if (allAgences.length > 0) setSelectedAgence(allAgences[0].agence_id);
    }).catch(() => toast.error('Erreur chargement agences'));
  }, []);

  // Charger jours fériés
  const loadJours = useCallback(async () => {
    if (!selectedAgence) return;
    setLoading(true);
    try {
      const res = await api.getJoursFeries(selectedAgence);
      const loaded = res.data || [];
      setJours(loaded);
      // Vérifier si l'année sélectionnée existe dans les données chargées
      const availableYears = [...new Set(loaded.map(j => yearOf(j.date)).filter(Boolean))];
      const currentYear = String(new Date().getFullYear());
      if (!availableYears.includes(selectedYear)) {
        // Garder l'année courante même si vide (pour saisie)
        setSelectedYear(availableYears[0] || currentYear);
      }
    } catch { toast.error('Erreur chargement jours fériés'); }
    finally { setLoading(false); }
  }, [selectedAgence]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadJours(); }, [loadJours]);

  // Années disponibles (depuis jours chargés + année courante toujours présente)
  const currentYear = String(new Date().getFullYear());
  const yearsSet    = new Set(jours.map(j => yearOf(j.date)).filter(Boolean));
  yearsSet.add(currentYear);
  const years = [...yearsSet]
    .sort((a, b) => b.localeCompare(a))
    .map(year => ({ year, count: jours.filter(j => yearOf(j.date) === year).length }));

  // Jours filtrés par année
  const joursFiltered = jours.filter(j => yearOf(j.date) === selectedYear);

  const handleAdd = () => {
    if (!newDate) { toast.error('Date obligatoire'); return; }
    if (!newNom.trim()) { toast.error('Libellé obligatoire'); return; }
    if (jours.find(j => j.date === newDate)) { toast.error('Cette date existe déjà'); return; }
    // Auto-basculer sur l'année de la date saisie
    const y = yearOf(newDate);
    setJours(prev => [...prev, { date: newDate, nom: newNom.trim() }].sort((a, b) => a.date.localeCompare(b.date)));
    if (y) setSelectedYear(y);
    setNewDate('');
    setNewNom('');
  };

  const handleRemove = (date) => {
    setJours(prev => prev.filter(j => j.date !== date));
  };

  const handleSave = async () => {
    if (!selectedAgence) return;
    setSaving(true);
    try {
      await api.setJoursFeries(selectedAgence, jours);
      toast.success('Jours fériés enregistrés');
    } catch { toast.error('Erreur enregistrement'); }
    finally { setSaving(false); }
  };

  // Quand l'année change → pré-remplir le champ date
  const handleYearSelect = (year) => {
    setSelectedYear(year);
    setNewDate(`${year}-01-01`);
    setNewNom('');
  };

  const agenceObj = agences.find(a => a.agence_id === selectedAgence);

  return (
    <div className="space-y-5 max-w-4xl">

      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <CalendarDays size={18} style={{ color: 'var(--primary-solid)' }} />
            <h2 className="text-lg font-bold" style={{ color: 'var(--text)' }}>Jours fériés</h2>
          </div>
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
            Définis par agence — utilisés pour le décompte des locations et les feuilles de présence.
          </p>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Sélecteur agence */}
          <select
            value={selectedAgence}
            onChange={e => { setSelectedAgence(e.target.value); setShowDuplicate(false); }}
            data-testid="select-agence-jours-feries"
            className="px-3 py-2 rounded-lg border text-sm"
            style={{ borderColor: 'var(--border)', background: 'white', color: 'var(--text)' }}
          >
            {agences.map(a => (
              <option key={a.agence_id} value={a.agence_id}>
                {a.nom}{a.societe_nom ? ` — ${a.societe_nom}` : ''}
              </option>
            ))}
          </select>

          {/* Dupliquer */}
          {agences.length > 1 && (
            <button
              onClick={() => setShowDuplicate(v => !v)}
              data-testid="show-duplicate-btn"
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm font-medium transition-colors hover:border-blue-400"
              style={{
                borderColor: showDuplicate ? 'var(--primary-solid)' : 'var(--border)',
                background: showDuplicate ? 'var(--primary-light, #EEF2FF)' : 'white',
                color: showDuplicate ? 'var(--primary-solid)' : 'var(--text)',
              }}>
              <Copy size={13} />
              Dupliquer
            </button>
          )}
        </div>
      </div>

      {/* Panneau duplication */}
      {showDuplicate && (
        <DuplicatePanel
          agences={agences}
          sourceAgence={selectedAgence}
          selectedYear={selectedYear}
          onClose={() => setShowDuplicate(false)}
        />
      )}

      {/* Layout principal : années + tableau */}
      <div className="flex gap-5 items-start">

        {/* Colonne années */}
        <YearSidebar
          years={years}
          selected={selectedYear}
          onSelect={handleYearSelect}
        />

        {/* Tableau */}
        <div className="flex-1 space-y-4">
          <div className="rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border)' }}>

            {/* Header table */}
            <div className="grid grid-cols-[1fr_2fr_auto] px-4 py-2 text-xs font-semibold uppercase tracking-wide"
              style={{ background: 'var(--bg-subtle)', borderBottom: '1px solid var(--border)', color: 'var(--text-muted)' }}>
              <span>Date</span>
              <span>Libellé</span>
              <span className="pr-1">{agenceObj ? agenceObj.nom : ''}</span>
            </div>

            {/* Lignes filtrées */}
            {loading ? (
              <div className="px-4 py-8 text-center text-sm" style={{ color: 'var(--text-muted)' }}>
                Chargement…
              </div>
            ) : joursFiltered.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm" style={{ color: 'var(--text-muted)' }}>
                Aucun jour férié configuré pour {selectedYear}
              </div>
            ) : joursFiltered.map(j => (
              <div key={j.date}
                className="grid grid-cols-[1fr_2fr_auto] items-center px-4 py-2.5 border-b"
                style={{ borderColor: 'var(--border-light)' }}>
                <span className="text-sm font-medium tabular-nums" style={{ color: 'var(--text)' }}>
                  {fmtDate(j.date)}
                </span>
                <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>{j.nom}</span>
                <button
                  onClick={() => handleRemove(j.date)}
                  data-testid={`remove-jour-${j.date}`}
                  className="p-1.5 rounded-lg hover:bg-red-50 transition-colors">
                  <Trash2 size={13} style={{ color: '#EF4444' }} />
                </button>
              </div>
            ))}

            {/* Ghost row — ajout */}
            <div className="grid grid-cols-[1fr_2fr_auto] items-center gap-2 px-3 py-2.5"
              style={{ background: 'var(--bg-subtle)' }}>
              <input
                type="date"
                value={newDate}
                onChange={e => setNewDate(e.target.value)}
                data-testid="new-jour-date"
                className="px-2.5 py-1.5 rounded-lg border text-sm"
                style={{ borderColor: 'var(--border)', background: 'white' }}
              />
              <input
                value={newNom}
                onChange={e => setNewNom(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleAdd()}
                placeholder="Ex: Abolition de l'esclavage"
                data-testid="new-jour-nom"
                className="px-2.5 py-1.5 rounded-lg border text-sm"
                style={{ borderColor: 'var(--border)', background: 'white' }}
              />
              <button
                onClick={handleAdd}
                data-testid="add-jour-btn"
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold text-white"
                style={{ background: 'var(--primary-gradient)' }}>
                <Plus size={12} /> Ajouter
              </button>
            </div>
          </div>

          {/* Bas de page : note + save */}
          <div className="flex items-center justify-between gap-4">
            <p className="text-xs" style={{ color: 'var(--text-disabled)' }}>
              Les jours fériés sont propres à chaque agence (calendriers Guadeloupe, Martinique, Guyane…)
            </p>
            <button
              onClick={handleSave}
              disabled={saving || !selectedAgence}
              data-testid="save-jours-feries-btn"
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-50"
              style={{ background: 'var(--primary-gradient)' }}>
              <Save size={15} />
              {saving ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
